/**
 * FileField - File upload + document viewer panel for ticket creation
 * Adapted from Thaklis app NewTicket/panels/FileField.js to use ThaklisApiService
 * (returns the FULL axios response, NOT unwrapped) and plain MUI components.
 *
 * Supports:
 *  - single-file upload (default)
 *  - multi-file upload for a single-instance document field (allowMultiple) — files accumulate into
 *    one value carrying `isNewUpload` + `documentReferences`, saved as one group at ticket submit
 *  - deferBulkUpload: skip the employee-documents bulk write at select time (submit does the write-back)
 */
import React, { useState, useRef } from 'react';
import { Box, Typography, Icon, CircularProgress, Button, Dialog, DialogContent, DialogTitle, IconButton, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ThaklisApiService from '../../../services/thaklis-api.service';
import { STAT_FIELDS, SPECIFIC_DOCUMENT_FIELDS } from '../constants';
import { isDocumentField, toBackendDocumentType, pxToRem } from '../utils';

const FileField = ({ field, onFileUpload, index, onFileRemove, isProcessed, employeeUid, selectedEmployee, contextProps, deferBulkUpload = false, allowMultiple = false }) => {
  const { shouldShowFieldInSection } = contextProps || {};

  const [openModal, setOpenModal] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [viewError, setViewError] = useState(null);

  const isDocField = field && field.name && isDocumentField(field.name);
  const isStaticDocField = field && field.name && SPECIFIC_DOCUMENT_FIELDS.includes(field.name);
  const isVisible = isDocField || isStaticDocField || (shouldShowFieldInSection && field && field.name && shouldShowFieldInSection(field.name, 'DOCUMENTS'));

  const [file, setFile] = useState();
  const [isFileUploading, setFileUploading] = useState(false);
  const [error] = useState(null);
  const fileInputRef = useRef(null);

  const getEmployeeUid = () => {
    if (selectedEmployee?.uid) return selectedEmployee.uid;
    if (employeeUid && employeeUid.startsWith('EMP-')) return employeeUid;
    return employeeUid || window.currentEmployeeUid || null;
  };

  const getDocumentType = () => {
    if (!field) return null;
    if (field.name === 'salaryCertificate') return 'salaryCertificate';
    if (field.value && field.value.documentType) return field.value.documentType;
    if (field.name) return field.name;
    return null;
  };

  const _onFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    const tooBig = selectedFiles.find((f) => f.size > maxSize);
    if (tooBig) {
      alert(`"${tooBig.name}" exceeds the 10MB limit. Please select a smaller file.`);
      return;
    }

    // Reset input so selecting the same file(s) again still fires onChange
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (allowMultiple) {
      uploadMultipleFiles(selectedFiles);
      return;
    }

    const selectedFile = selectedFiles[0];
    setFile(selectedFile);
    uploadFile(selectedFile);
  };

  // Upload several files for a single-instance document field and accumulate them into one value.
  // Files go to S3 only (bulk write-back to the employee profile is deferred to ticket submit). The
  // value carries an `isNewUpload` flag + `documentReferences` array so the multi-file display renders
  // every file and the submit-time sync saves them all as one group. Re-selecting appends.
  const uploadMultipleFiles = async (filesToUpload) => {
    const currentEmployeeUid = getEmployeeUid();
    if (!currentEmployeeUid) {
      alert('Cannot upload file: No employee selected. Please select an employee first.');
      return;
    }
    const documentType = getDocumentType();
    const backendDocumentType = toBackendDocumentType(documentType);

    setFileUploading(true);
    try {
      const existing = (field?.value && field.value.isNewUpload && Array.isArray(field.value.documentReferences))
        ? field.value.documentReferences
        : [];
      const uploaded = [];
      for (const fileToUpload of filesToUpload) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', fileToUpload);
        uploadFormData.append('documentType', backendDocumentType);
        uploadFormData.append('employeeUid', currentEmployeeUid);
        uploadFormData.append('fileName', fileToUpload.name);
        // eslint-disable-next-line no-await-in-loop
        const uploadAxiosResp = await ThaklisApiService.post('v1/files/upload', uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const uploadResult = uploadAxiosResp.data || {};
        const filePath = uploadResult.filePath || uploadResult.data?.filePath || uploadResult.path || uploadResult.data?.path;
        if (!filePath) continue;
        uploaded.push({
          filePath,
          fileName: fileToUpload.name,
          contentType: fileToUpload.type || null,
          fileSize: fileToUpload.size || null,
        });
      }

      const allRefs = [...existing, ...uploaded].map((r, i) => ({ ...r, isPrimary: i === 0 }));
      if (allRefs.length === 0) return;
      const primary = allRefs[0];
      const value = {
        isNewUpload: true,
        documentType: backendDocumentType,
        documentGroupId: null,
        id: primary.filePath,
        fileName: primary.fileName,
        filePath: primary.filePath,
        documentReferences: allRefs,
        hasMultipleFiles: allRefs.length > 1,
        totalFiles: allRefs.length,
      };
      if (onFileUpload) onFileUpload(value, field);
    } catch (err) {
      console.error('Error during multi-file upload:', err);
      alert(`Error uploading file: ${err.message}`);
    } finally {
      setFileUploading(false);
    }
  };

  // Single-file upload. Uses 2-API flow: /v1/files/upload then optionally /v1/employee-documents/{id}/bulk.
  const uploadFile = async (fileToUpload) => {
    if (!fileToUpload) return;
    setFileUploading(true);

    try {
      const currentEmployeeUid = getEmployeeUid();
      if (!currentEmployeeUid) {
        alert('Cannot upload file: No employee selected. Please select an employee first.');
        setFileUploading(false);
        return;
      }

      const documentType = getDocumentType();
      const backendDocumentType = toBackendDocumentType(documentType);
      const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(field.name);

      // Step 1: Upload the file
      const uploadFormData = new FormData();
      uploadFormData.append('file', fileToUpload);
      uploadFormData.append('documentType', backendDocumentType);
      uploadFormData.append('employeeUid', currentEmployeeUid);
      uploadFormData.append('fileName', fileToUpload.name);

      const uploadAxiosResp = await ThaklisApiService.post('v1/files/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadResult = uploadAxiosResp.data || {};
      const filePath = uploadResult.filePath || uploadResult.data?.filePath || uploadResult.path || uploadResult.data?.path;
      if (!filePath) throw new Error('Failed to get filePath from upload response');

      // Step 2: Save document to employee profile (unless deferred to submit-time write-back)
      let documentGroupId = null;
      let bulkDocumentId = null;

      if (!deferBulkUpload && currentEmployeeUid && backendDocumentType && isDocumentField(field.name) && !isDynamicServiceField) {
        const bulkPayload = {
          documentType: backendDocumentType,
          replaceExisting: false,
          documents: [{ filePath, fileName: fileToUpload.name, fileSize: fileToUpload.size }]
        };

        // Multiple-instance certificate types are keyed by a document name (title). There is no name
        // prompt during ticket creation, so derive one from the file name.
        const MULTI_INSTANCE_DOC_TYPES = ['educational_certificate', 'experience_certificate'];
        if (MULTI_INSTANCE_DOC_TYPES.includes(backendDocumentType)) {
          const baseName = fileToUpload.name.replace(/\.[^/.]+$/, '').trim();
          bulkPayload.title = baseName || fileToUpload.name;
          bulkPayload.isReplace = false;
        }

        try {
          const bulkResp = await ThaklisApiService.post(`v1/employee-documents/${currentEmployeeUid}/bulk`, bulkPayload);
          const bulkResult = bulkResp.data?.data || bulkResp.data || [];
          if (Array.isArray(bulkResult) && bulkResult.length > 0) {
            documentGroupId = bulkResult[0].documentGroupId;
            bulkDocumentId = bulkResult[0].id;
          }
        } catch (bulkError) {
          console.error('Bulk API error:', bulkError);
        }
      }

      const fileMetadata = {
        id: bulkDocumentId || filePath,
        fileName: fileToUpload.name,
        filePath,
        fileSize: fileToUpload.size,
        fileType: fileToUpload.type,
        documentType: backendDocumentType,
        documentGroupId,
        uploadTime: new Date().toISOString()
      };

      if (onFileUpload) onFileUpload(fileMetadata, field);
    } catch (err) {
      console.error('Error during file upload:', err);
      alert(`Error uploading file: ${err.message}`);
    } finally {
      setFileUploading(false);
    }
  };

  const _onFileRemove = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    // Pass the field object first (index.js handlers key off field.id/field.name), index second.
    if (onFileRemove) onFileRemove(field, index);
  };

  // Documents attached to this field (auto-filled references or an uploaded set).
  const documentReferences = field?.value?.documentReferences || [];

  const handleViewDocument = async (doc) => {
    setDocumentTitle(`${doc.fileName || 'Document'} - ${field?.title || field?.name || 'Document'}`);
    setOpenModal(true);
    setIsLoading(true);
    setViewError(null);
    setDocumentData(null);

    try {
      let downloadUrl = null;
      if (doc.filePath) {
        downloadUrl = `v1/files/download?filePath=${encodeURIComponent(doc.filePath)}`;
      } else if (doc.documentUrl) {
        downloadUrl = doc.documentUrl.replace(/^\//, '');
      } else if (doc.employeeUid && doc.documentType) {
        downloadUrl = `v1/users/${doc.employeeUid}/documents/${doc.documentType}/download`;
      }
      if (!downloadUrl) throw new Error('No download URL available');

      const response = await ThaklisApiService.get(downloadUrl, { responseType: 'blob' });
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      setDocumentData({ url, contentType: blob.type });
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading document:', err);
      setViewError(err.message || 'Failed to load document');
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    if (documentData?.url) {
      URL.revokeObjectURL(documentData.url);
      setDocumentData(null);
    }
  };

  if (!isVisible && field?.category !== undefined) return null;

  // Files to show: auto-filled references (single or multiple), or a single uploaded/auto-filled file.
  const displayFiles = (() => {
    if (Array.isArray(documentReferences) && documentReferences.length > 0) return documentReferences;
    const v = field?.value;
    if (v && typeof v === 'object' && (v.filePath || v.fileName)) {
      return [{ id: v.id, fileName: v.fileName, filePath: v.filePath, isPrimary: true }];
    }
    return [];
  })();
  const hasDocument = displayFiles.length > 0;
  const fieldLabelText = (field?.name && STAT_FIELDS[field.name]?.label) || field?.label || field?.title || field?.name || 'Upload Document';
  const isMandatory = field?.mandatory || field?.required;

  let acceptedType = 'image/*';
  const _fieldType = field?.type?.toLowerCase?.() || '';
  if (_fieldType === 'pdf') {
    acceptedType = 'application/pdf,image/*';
  } else if (_fieldType === 'document') {
    acceptedType = '.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/*';
  }

  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 500, color: 'text.primary' }}>
        {fieldLabelText}{isMandatory ? ' *' : ''}
      </Typography>

      {hasDocument ? (
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {displayFiles.map((doc, i) => {
              const showPrimary = doc.isPrimary && displayFiles.length > 1;
              return (
                <Box key={doc.id || doc.documentId || doc.filePath || `doc-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon fontSize="small" sx={{ color: showPrimary ? 'warning.main' : 'action.active' }}>
                    {showPrimary ? 'star' : 'description'}
                  </Icon>
                  <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                    {doc.fileName || 'Document'}{showPrimary ? ' (Primary)' : ''}
                  </Typography>
                  {doc.filePath && (
                    <Button variant="outlined" size="small" onClick={() => handleViewDocument(doc)} sx={{ minWidth: '56px', height: '28px', fontSize: '0.75rem' }}>
                      VIEW
                    </Button>
                  )}
                </Box>
              );
            })}
            {isFileUploading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>Uploading…</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
            <Button variant="text" size="small" component="label" disabled={isFileUploading} sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
              {allowMultiple ? 'Add / Replace' : 'Replace'}
              <input type="file" hidden multiple={allowMultiple} onChange={_onFileSelect} accept={acceptedType} ref={fileInputRef} />
            </Button>
            <Button variant="text" size="small" onClick={_onFileRemove} disabled={isFileUploading} sx={{ fontSize: '0.7rem', textTransform: 'none', color: 'error.main' }}>
              Remove
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          display="flex" alignItems="center" justifyContent="space-between" px={1.5}
          sx={{ border: 1, borderColor: 'grey.400', borderRadius: pxToRem(6), minHeight: pxToRem(45) }}
        >
          <Button variant="text" component="label" disabled={isFileUploading} sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
            {isFileUploading ? 'Uploading…' : (file ? file.name : `Select ${field?.type || 'file'}`)}
            <input type="file" hidden multiple={allowMultiple} onChange={_onFileSelect} accept={acceptedType} ref={fileInputRef} />
          </Button>
          {isFileUploading && <CircularProgress size={16} />}
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', color: 'error.main' }}>
          <Icon fontSize="small" color="error">error_outline</Icon>
          <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>{error}</Typography>
        </Box>
      )}

      {/* Document Viewing Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{documentTitle}</Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isLoading && <CircularProgress />}
          {viewError && !isLoading && (
            <Typography color="error">{viewError}</Typography>
          )}
          {documentData && !isLoading && !viewError && (
            documentData.contentType?.startsWith('image/') ? (
              <img src={documentData.url} alt={documentTitle} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
            ) : (
              <iframe src={documentData.url} title={documentTitle} style={{ width: '100%', height: '70vh', border: 'none' }} />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileField;
